import type { Session } from "@supabase/supabase-js";
import { getClientSession, signOutClientLocal } from "@/services/auth/clientSession";
import { devLog } from "@/utils/devLog";
import { shouldLocalSignOutOnSessionError } from "@/utils/authBootstrapPolicy";

export interface AuthBootstrapSessionActions {
  setSession: (session: Session) => void;
  setUser: (user: Session["user"]) => void;
  setLoading: (loading: boolean) => void;
  setSessionRestorePending: (pending: boolean) => void;
}

/**
 * Handle Initial Get Session Result.
 */
export function handleInitialGetSessionResult(
  session: Session | null,
  error: Error | null,
  mounted: boolean,
  actions: AuthBootstrapSessionActions
): void {
  if (error) {
    console.error("❌ Error getting session:", error);
    if (shouldLocalSignOutOnSessionError(error)) {
      void signOutClientLocal();
      return;
    }
    if (mounted) {
      actions.setLoading(false);
    }
    return;
  }

  if (!mounted) return;

  if (session) {
    actions.setSessionRestorePending(true);
    devLog.warn(
      "ℹ️ Initial session exists, waiting for auth state change handler:",
      session.user.email
    );
    return;
  }

  devLog.warn("ℹ️ No initial session");
  actions.setLoading(false);
}

/**
 * Recover Session After Restore Timeout.
 */
export async function recoverSessionAfterRestoreTimeout(
  mounted: boolean,
  actions: AuthBootstrapSessionActions
): Promise<void> {
  try {
    const {
      data: { session: recoveredSession },
      error,
    } = await getClientSession();

    if (error && shouldLocalSignOutOnSessionError(error)) {
      await signOutClientLocal();
      return;
    }

    if (mounted && recoveredSession) {
      actions.setSession(recoveredSession);
      actions.setUser(recoveredSession.user);
    }
  } catch (recoverError) {
    devLog.warn("Session restore retry failed", recoverError);
  } finally {
    if (mounted) {
      actions.setSessionRestorePending(false);
      actions.setLoading(false);
    }
  }
}

export function shouldReleaseAuthBootstrapLoading(
  mounted: boolean,
  sessionRestorePending: boolean
): boolean {
  return mounted && !sessionRestorePending;
}

export function shouldRetrySessionRestore(
  mounted: boolean,
  sessionRestorePending: boolean
): boolean {
  return mounted && sessionRestorePending;
}

export interface AuthBootstrapTimers {
  authBootstrapTimeout: ReturnType<typeof setTimeout>;
  sessionRestoreFailSafe: ReturnType<typeof setTimeout>;
}

/**
 * Schedule Auth Bootstrap Timers.
 */
export function scheduleAuthBootstrapTimers(options: {
  mountedRef: () => boolean;
  getSessionRestorePending: () => boolean;
  setLoading: (loading: boolean) => void;
  onSessionRestoreTimeout: () => void;
  bootstrapMs: number;
  sessionRestoreMs: number;
}): AuthBootstrapTimers {
  const authBootstrapTimeout = setTimeout(() => {
    if (
      shouldReleaseAuthBootstrapLoading(options.mountedRef(), options.getSessionRestorePending())
    ) {
      devLog.warn("Auth bootstrap slow — releasing loading gate");
      options.setLoading(false);
    }
  }, options.bootstrapMs);

  const sessionRestoreFailSafe = setTimeout(() => {
    if (shouldRetrySessionRestore(options.mountedRef(), options.getSessionRestorePending())) {
      devLog.warn("Session restore slow — retrying getSession");
      options.onSessionRestoreTimeout();
    }
  }, options.sessionRestoreMs);

  return { authBootstrapTimeout, sessionRestoreFailSafe };
}

export function handleGetSessionCatch(
  error: unknown,
  mounted: boolean,
  setLoading: (v: boolean) => void
): void {
  console.error("❌ getSession failed:", error);
  if (mounted) {
    setLoading(false);
  }
}
